import React from 'react';
import { motion } from 'framer-motion';

interface PixarBlurTextProps {
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  duration?: number;
  glowColor?: string;
  splitWords?: boolean;
  shimmer?: boolean;
}

/**
 * PixarBlurText — Cinematic Optical Camera Rack-Focus Blur Engine.
 *
 * Simulates a high-end 35mm f/1.2 cinema lens dialing from extreme bokeh defocus
 * into pin-sharp focus, accompanied by a luminous chromatic starlight bloom
 * and a traveling specular sheen.
 */
export function PixarBlurText({
  children,
  as = 'div',
  className = '',
  style = {},
  delay = 0.1,
  duration = 0.95,
  glowColor = 'rgba(249, 115, 22, 0.55)',
  splitWords = false,
  shimmer = false,
}: PixarBlurTextProps) {
  const Component = motion[as] as any;

  if (splitWords && typeof children === 'string') {
    const words = children.split(' ');
    return (
      <Component className={`inline-flex flex-wrap gap-x-2 ${className}`} style={style}>
        {words.map((word, index) => (
          <motion.span
            key={`${word}-${index}`}
            initial={{
              filter: 'blur(26px) brightness(2.2)',
              opacity: 0,
              scale: 1.12,
              letterSpacing: '0.15em',
            }}
            whileInView={{
              filter: 'blur(0px) brightness(1.0)',
              opacity: 1,
              scale: 1.0,
              letterSpacing: 'normal',
            }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration,
              delay: delay + index * 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="inline-block"
            style={{
              textShadow: `0 2px 14px rgba(0,0,0,0.9), 0 0 25px ${glowColor}`,
            }}
          >
            {word}
          </motion.span>
        ))}
      </Component>
    );
  }

  return (
    <>
      <Component
        initial={{
          filter: 'blur(28px) brightness(2.4)',
          opacity: 0,
          scale: 1.08,
          letterSpacing: '0.18em',
        }}
        whileInView={{
          filter: 'blur(0px) brightness(1.0)',
          opacity: 1,
          scale: 1.0,
          letterSpacing: 'normal',
        }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{
          duration,
          delay,
          ease: [0.16, 1, 0.3, 1],
        }}
        className={`${className} ${shimmer ? 'pixar-shimmer-active' : ''}`}
        style={{
          textShadow: `0 2px 20px rgba(0,0,0,0.95), 0 0 30px ${glowColor}`,
          transform: 'translateZ(0)',
          willChange: 'filter, transform',
          backfaceVisibility: 'hidden',
          ...style,
        }}
      >
        {children}
      </Component>
    </>
  );
}

/**
 * PixarFloatingBadge — An illuminated translucent glass badge that racks into focus.
 */
export function PixarFloatingBadge({
  children,
  className = '',
  delay = 0.05,
  icon = '✨',
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  icon?: string;
}) {
  return (
    <motion.div
      initial={{ filter: 'blur(20px)', opacity: 0, scale: 0.88 }}
      whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/50 bg-slate-900/60 backdrop-blur-xl shadow-[0_0_24px_rgba(249,115,22,0.35)] text-amber-200 text-xs font-black tracking-widest uppercase ${className}`}
    >
      <span className="text-sm">{icon}</span>
      <span>{children}</span>
    </motion.div>
  );
}
